import { Injectable, OnDestroy } from '@angular/core';
import { ReportExportViewService } from '../../../../../lib/report-export-view.service';
import { digger } from '@cbre/digger';
import { ReportImagesService } from '../../../../../lib/report-images.service';
import { ReportAllRecordsImage } from '../../../../../lib/models/template.model';
import { ReportRecord } from '../../../../../lib/models/report.model';
import {
  PACIFIC_AVAILABILITIES_IDS,
  PACIFIC_TEMPLATES_IMAGE_ORDER,
} from '../../../../../lib/shared/templates/template-blueprints.const';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { forkJoin, Observable, of } from 'rxjs';
import { filter } from 'rxjs/operators';
import { getPrimaryImageOrFirst, isMediaImage, isMediaInfoPublishable, PropertyMediaInfo } from '@miq/utils';
import { groupByProperty } from '../../../../shared/utils/group-by-property';

const AVAILABILITY_MEDIA_PATH = 'data.property.property_media_information';
const PROPERTY_MEDIA_PATH = 'associatedProperty.property_media_information';

@UntilDestroy()
@Injectable()
export class ReportResetImagesService implements OnDestroy {
  reportAllImages: ReportAllRecordsImage;
  reportId: number;

  constructor(
    private reportExportViewService: ReportExportViewService,
    private reportImagesService: ReportImagesService
  ) {
    this.reportImagesService.onUpdateAdditionalImage$.pipe(untilDestroyed(this)).subscribe(response => {
      this.reportAllImages = response;
    });
  }

  ngOnDestroy() {
    // required to destroy the Subscription.
  }

  public getAdditionalAndSecondaryImages(
    reportRecordData: ReportRecord | ReportRecord[],
    reportId: number,
    isProperty: boolean,
    isFirstTime = false
  ): Observable<[number, number]>[] {
    const path = isProperty ? PROPERTY_MEDIA_PATH : AVAILABILITY_MEDIA_PATH;
    if (reportId == null || path == null) {
      return null;
    }
    this.reportId = reportId;
    let records = Array.isArray(reportRecordData) ? reportRecordData : [reportRecordData];
    if (isFirstTime && isProperty) {
      records = groupByProperty(records) as ReportRecord[];
    }
    let setRecordImages$: Observable<[number, number]>[] = [];
    (records as ReportRecord[]).forEach(record => {
      const edpRecordId =
        isProperty && record.associatedProperty ? record.associatedProperty.entity.id : -record.data.entity.id;
      let mediaInfo = digger(record, path);
      if (mediaInfo.length) {
        const primaryImage = getPrimaryImageOrFirst(mediaInfo);
        if (primaryImage) {
          mediaInfo = [
            ...mediaInfo.filter(
              (media: PropertyMediaInfo) =>
                media.id !== primaryImage.id && isMediaImage(media) && isMediaInfoPublishable(media)
            ),
          ];
        }
      }
      if (isProperty) {
        // If report is Property image order, sort
        mediaInfo.sort(this.sortImages);
      }
      if (isFirstTime) {
        setRecordImages$.push(
          this.setRecordImages(reportId, edpRecordId, mediaInfo, true).pipe(filter<[number, number]>(Boolean))
        );
      } else {
        if (this.reportAllImages[edpRecordId] && this.reportAllImages[edpRecordId].length) {
          setRecordImages$.push(
            this.resetRecordImages(reportId, edpRecordId, mediaInfo).pipe(filter<[number, number]>(Boolean))
          );
        } else {
          setRecordImages$.push(
            this.setRecordImages(reportId, edpRecordId, mediaInfo).pipe(filter<[number, number]>(Boolean))
          );
        }
      }
    });
    return setRecordImages$;
    //return forkJoin(setRecordImages$).pipe(tap(this.reportAllRecordImages.bind(this)));
  }

  /**
   * Set the additional and secondary images on a report
   * @param reportRecordData
   * @param reportId
   * @param templateId
   * @returns
   */
  public setReportAdditionalImages(
    reportRecordData: ReportRecord | ReportRecord[],
    reportId: number,
    templateId: number
  ): Observable<[number, number][]> {
    let availabilityImages: Observable<[number, number]>[] = [];
    const propertyImages = this.getAdditionalAndSecondaryImages(reportRecordData, reportId, true, true);
    if (PACIFIC_AVAILABILITIES_IDS.includes(templateId)) {
      availabilityImages = this.getAdditionalAndSecondaryImages(reportRecordData, reportId, false, true);
    }
    return forkJoin([...propertyImages, ...availabilityImages]);
  }

  /**
   * Update report record with additional images id's
   */
  private setRecordImages(
    reportId: number,
    edpRecordId: number,
    mediaInfo: any[],
    isFirstTime = false
  ): Observable<number[]> {
    if (!Array.isArray(mediaInfo) || mediaInfo.length === 0) {
      return of([]);
    }
    let setImages = [];
    //With edpRecordId check if there is an image (Additional)
    if (isFirstTime || !this.reportAllImages[edpRecordId] || !this.reportAllImages[edpRecordId][0]) {
      setImages.push({ edpRecordId, imageId: mediaInfo[0].id });
    }
    if (mediaInfo.length > 1) {
      //With edpRecordId check if there is an extra image (Secondary)
      if (isFirstTime || !this.reportAllImages[edpRecordId] || !this.reportAllImages[edpRecordId][1]) {
        setImages.push({ edpRecordId, imageId: mediaInfo[1].id });
      }
    }
    return setImages.length ? this.reportExportViewService.addAdditionalImage(reportId, setImages) : of([]);
  }

  /**
   * Reset additional and secondary images to a report
   * @param reportRecordData
   * @param reportId
   * @param templateId
   * @returns
   */
  public resetReportAdditionalImages(
    reportRecordData: ReportRecord | ReportRecord[],
    reportId: number,
    templateId: number
  ): Observable<[number, number][]> {
    const resetImages = this.getAdditionalAndSecondaryImages(
      reportRecordData,
      reportId,
      !PACIFIC_TEMPLATES_IMAGE_ORDER.AVAILABILITY_ORDER.includes(templateId)
    );
    return forkJoin(resetImages);
  }

  /**
   * Reset report record with additional images id's
   */
  private resetRecordImages(reportId: number, edpRecordId: number, mediaInfo: any[]): Observable<any> {
    if (edpRecordId == null || !Array.isArray(mediaInfo) || mediaInfo.length === 0) {
      return of(null);
    }
    let resetImages$: Observable<number>[] = [];
    if (
      !!this.reportAllImages[edpRecordId][0] &&
      !!this.reportAllImages[edpRecordId][0].imageId &&
      this.reportAllImages[edpRecordId][0].imageId !== mediaInfo[0].id
    ) {
      const reportImageId = this.reportAllImages[edpRecordId][0].reportImageId;
      resetImages$.push(this.reportExportViewService.changeAdditionalImage(reportId, reportImageId, mediaInfo[0].id));
    }
    if (mediaInfo.length > 1) {
      if (
        !!this.reportAllImages[edpRecordId][1] &&
        !!this.reportAllImages[edpRecordId][1].imageId &&
        this.reportAllImages[edpRecordId][1].imageId !== mediaInfo[1].id
      ) {
        const reportImageId = this.reportAllImages[edpRecordId][1].reportImageId;
        resetImages$.push(this.reportExportViewService.changeAdditionalImage(reportId, reportImageId, mediaInfo[1].id));
      } else if (!!this.reportAllImages[edpRecordId] && this.reportAllImages[edpRecordId].length === 1) {
        resetImages$.push(
          this.reportExportViewService.addAdditionalImage(reportId, [{ edpRecordId, imageId: mediaInfo[1].id }])
        );
      }
    }
    return forkJoin(resetImages$);
  }

  /**
   * Sort by id first and display_order if exists
   */
  private sortImages(a, b) {
    if (a.display_order > b.display_order) return 1;
    if (a.display_order < b.display_order) return -1;
    if (a.id > b.id) return 1;
    if (a.id < b.id) return -1;
  }
}
